import DashboardCard from "../../../ISSB/Dashboard/components/DashboardCard";
import LocalSpinner from "../../../../components/LocalSpinner";
import { Box, Flex, HStack, Text } from "@chakra-ui/react";
import { getFormattedNumValue } from "../../../../utils";
import { ArrowDown, ArrowUp } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../../config";
import BigNumber from "../components/BigNumber";

// Up=green variance lozenge — the INVERSE of the shared YoYChip (which treats
// down=green as an improvement). For Data Quality, MORE suppliers reporting
// year-on-year is the improvement, so a rising figure should read green. The
// arrow still points in the true direction of change (up when it rose), so we
// can't reuse YoYChip with a negated value (that would flip the arrow and
// mislead). Shape/padding mirror YoYChip for visual consistency.
const ReportingChip = ({ pctChange }) => {
  if (pctChange == null || !Number.isFinite(pctChange)) return null;
  const improved = pctChange >= 0; // more reporting suppliers = good
  const bg = improved ? "green.50" : "red.50";
  const color = improved ? "green.700" : "red.600";
  const Arrow = pctChange >= 0 ? ArrowUp : ArrowDown;
  return (
    <HStack spacing={1} bg={bg} color={color} px={2} py={0.5} borderRadius="full" flexShrink={0} w="fit-content">
      <Arrow size={11} weight="bold" />
      <Text fontSize="11px" fontWeight="600">
        {Math.abs(pctChange).toFixed(1)}&nbsp;%
      </Text>
    </HStack>
  );
};

const DataQualityCard = ({ isLoading, currentYear, previousYear, theme = "light" }) => {
  const navigate = useNavigate();
  const colors = {
    light: { cardBg: "#F7FAFC", title: "teal", value: "gray.800" },
    dark: { cardBg: "pureBlack", title: "pureWhite", value: "lightLime" },
  };
  const t = colors[theme] || colors.light;

  const totalSuppliersWithData = currentYear?.totalSuppliersWithData || 0;
  const supplierCountWithRequestedData = currentYear?.supplierCountWithRequestedData || 0; // prevent division by zero
  const totalSuppliersWithDataPercentage = // this is supplier with any data finalized
    supplierCountWithRequestedData > 0 ? (totalSuppliersWithData / supplierCountWithRequestedData) * 100 : 0;

  const totalSuppliersWithAllFinalized = currentYear?.totalSuppliersWithAllFinalizedData || 0;
  const totalSuppliersWithDataPercentageFinalized = // this is supplier with everything finalized
    supplierCountWithRequestedData > 0 ? (totalSuppliersWithAllFinalized / supplierCountWithRequestedData) * 100 : 0;

  // this is supplier that have incomplete data (in progress) = sup with requested - sup with all data
  const totalSuppliersWithIncompleteData = supplierCountWithRequestedData - totalSuppliersWithAllFinalized;
  const totalSuppliersWithIncompleteDataPercentage =
    supplierCountWithRequestedData > 0 ? (totalSuppliersWithIncompleteData / supplierCountWithRequestedData) * 100 : 0;

  const verifiedSupplierCount = currentYear?.verifiedSupplierCount || 0;
  const verifiedSupplierPercentage =
    totalSuppliersWithData > 0 ? (verifiedSupplierCount / totalSuppliersWithData) * 100 : 0;

  let basePercentageChange = 0;
  if (previousYear && previousYear.totalSuppliersWithData) {
    const previousSuppliersWithData = previousYear?.totalSuppliersWithData || 0;
    basePercentageChange = ((totalSuppliersWithData - previousSuppliersWithData) / previousSuppliersWithData) * 100;
  }

  return (
    <DashboardCard bg={t.cardBg} px={6} onClick={() => navigate(routes.supplierInsightsEngagement)}>
      <Text fontSize="body.2xl" fontWeight="bold" color={t.title}>
        Data Quality
      </Text>
      {isLoading ? (
        <LocalSpinner size="sm" />
      ) : (
        <Flex direction="column" gap={6} height="100%" justify="space-between">
          <Box>
            {[
              { pct: totalSuppliersWithDataPercentageFinalized, label: "finalized" },
              { pct: totalSuppliersWithIncompleteDataPercentage, label: "in progress" },
              { pct: verifiedSupplierPercentage, label: "verified" },
            ].map((row) => (
              <Flex key={row.label} align="baseline" gap={2} py={1.5}>
                <BigNumber color={t.value}>{getFormattedNumValue(row.pct, 2) || 0} %</BigNumber>
                <Text fontSize="body.s" color="gray.500">
                  {row.label}
                </Text>
              </Flex>
            ))}
          </Box>

          <Box>
            <Text fontSize="body.s" fontWeight="semibold" color="gray.500" mb={1}>
              Suppliers reporting vs last year
            </Text>
            {/* Up=green here: an increase in reporting suppliers is the
                improvement, opposite to the emissions cards. */}
            <ReportingChip pctChange={basePercentageChange} />
          </Box>
        </Flex>
      )}
    </DashboardCard>
  );
};

export default DataQualityCard;
